const isRemoteUrl = (value) =>
  typeof value === "string" && /^https?:\/\//i.test(value);

export const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () =>
      reject(reader.error || new Error("Could not read the carousel image"));
    reader.readAsDataURL(blob);
  });

export const getUploadPreviewSource = async (selection) => {
  if (selection?.file) {
    return blobToDataUrl(selection.file);
  }

  return selection?.preview_url || selection?.url || "";
};

export const fetchImageAsDataUrl = async (url) => {
  const response = await fetch(url, {
    mode: "cors",
    cache: "no-store",
    credentials: "omit",
  });

  if (!response.ok) {
    throw new Error(`Could not load carousel image (${response.status})`);
  }

  return blobToDataUrl(await response.blob());
};

const getElementImage = (element) =>
  element?.value && typeof element.value === "object"
    ? element.value.image
    : element?.value;

const withPreparedImage = (element, image, sourceUrl) => ({
  ...element,
  _preview_source_url: sourceUrl,
  value:
    element.value && typeof element.value === "object"
      ? { ...element.value, image }
      : image,
});

const prepareElementImage = async (element, loadImage) => {
  const sourceUrl = getElementImage(element);
  const shouldPrepare =
    ["image", "logo", "background"].includes(element?.type) &&
    element?.media_id &&
    isRemoteUrl(sourceUrl);

  if (!shouldPrepare) return element;

  try {
    return withPreparedImage(element, await loadImage(sourceUrl), sourceUrl);
  } catch (error) {
    console.warn("Could not prepare carousel image", error);
    return element;
  }
};

export const prepareCarouselMediaImages = async (carousel) => {
  const commonSettings = carousel?.common_settings;
  const commonElements = commonSettings?.elements;
  const slides = carousel?.slides;

  if (!Array.isArray(commonElements) && !Array.isArray(slides)) return carousel;

  const loadedImages = new Map();
  const loadImage = (url) => {
    if (!loadedImages.has(url)) {
      loadedImages.set(url, fetchImageAsDataUrl(url));
    }
    return loadedImages.get(url);
  };

  const [previewSlides, previewCommonElements] = await Promise.all([
    Promise.all(
      (slides || []).map(async (slide) => ({
        ...slide,
        elements: await Promise.all(
          (slide?.elements || []).map((element) =>
            prepareElementImage(element, loadImage)
          )
        ),
      }))
    ),
    Promise.all(
      (commonElements || []).map((element) =>
        prepareElementImage(element, loadImage)
      )
    ),
  ]);

  return {
    ...carousel,
    slides: previewSlides,
    common_settings: {
      ...commonSettings,
      elements: previewCommonElements,
    },
  };
};

export const prepareBrandKitProfileImages = async (brandKits = []) =>
  Promise.all(
    brandKits.map(async (brandKit) => {
      const mediaId = brandKit?.media_attachments?.[0]?.id;
      if (!mediaId || !isRemoteUrl(brandKit?.logo)) return brandKit;

      try {
        return {
          ...brandKit,
          logo: await fetchImageAsDataUrl(brandKit.logo),
        };
      } catch (error) {
        console.warn("Could not prepare brand kit profile image", error);
        return brandKit;
      }
    })
  );
